import {
  ArrowLeft,
  Search,
  MapPin,
  Shield,
  Mail,
  Phone,
  Building2,
  Filter,
  BadgeCheck,
  Briefcase,
  X,
  Home,
  MessageSquare,
  User,
} from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "../components/ui/card";
import { Input } from "../components/ui/input";
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
} from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import { useNavigate } from "react-router-dom";
import { useState, useMemo } from "react";
import { useQuery } from "@tanstack/react-query";
import MemberService from "../services/APIs/MemberService";
import MemberProfileService from "../services/APIs/MemberProfileService";
import CitycollabNavigationRoutes from "../utils/navigationRoutes";

// MainNavigation component
const MainNavigation = () => {
  const navigate = useNavigate();
  return (
    <nav className="fixed bottom-0 left-0 right-0 z-50 border-t border-border bg-card lg:hidden">
      <div className="grid grid-cols-3 gap-1 p-2">
        <Button
          variant="ghost"
          size="sm"
          className="flex h-auto flex-col gap-1 py-2"
          onClick={() => navigate(CitycollabNavigationRoutes.NEW_DESIGN)}
        >
          <Home className="h-5 w-5 text-primary" />
          <span className="text-[10px] font-medium text-primary">Home</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="flex h-auto flex-col gap-1 py-2"
          onClick={() => navigate(CitycollabNavigationRoutes.MESSAGES)}
        >
          <MessageSquare className="h-5 w-5" />
          <span className="text-[10px]">Messages</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="flex h-auto flex-col gap-1 py-2"
          onClick={() => navigate(CitycollabNavigationRoutes.PROFILE)}
        >
          <User className="h-5 w-5" />
          <span className="text-[10px]">Profile</span>
        </Button>
      </div>
    </nav>
  );
};

const Directory = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedExpertise, setSelectedExpertise] = useState<string[]>([]);
  const [verifiedOnly, setVerifiedOnly] = useState(false);
  const [nearbyOnly, setNearbyOnly] = useState(false); // Organization Only toggle

  // Get organization ID from localStorage
  const organizationId = localStorage.getItem("OrganizationId");

  // Fetch government skills from API
  const { data: govSkillsData, isLoading: isLoadingSkills } = useQuery({
    queryKey: ["gov-skills"],
    queryFn: async () => {
      try {
        return await MemberProfileService.getGovSkills();
      } catch (error) {
        console.error("Error fetching gov skills:", error);
        return [];
      }
    },
    enabled: true,
  });

  // Fetch members from API
  const { data: membersResponse, isLoading: isLoadingMembers } = useQuery({
    queryKey: [
      "memberDirectoryList",
      nearbyOnly,
      verifiedOnly,
    ],
    queryFn: async () => {
      const payload: {
        my_organization: boolean;
        member_status: boolean;
      } = {
        my_organization: nearbyOnly,
        member_status: verifiedOnly,
      };

      const response =
        await MemberService.getMemberDirectoryListFilters(payload);

      // Extract members array from various possible response structures
      let members: any[] = [];
      if (Array.isArray(response)) {
        members = response;
      } else if (
        response?.results?.data &&
        Array.isArray(response.results.data)
      ) {
        members = response.results.data;
      } else if (response?.data && Array.isArray(response.data)) {
        members = response.data;
      } else if (response?.results && Array.isArray(response.results)) {
        members = response.results;
      }

      // Extract stats from API response or calculate from members array
      return {
        members: members,
        total_members_count: response?.total_members_count ?? members.length,
        verified_members_count:
          response?.verified_members_count ??
          members.filter((m: any) => m.verified ?? m.member_verified).length,
      };
    },
    enabled: true,
  });

  // Extract members data and stats from response
  const totalMembersCount = membersResponse?.total_members_count ?? 0;
  const verifiedMembersCount = membersResponse?.verified_members_count ?? 0;

  // Map API response to employee format for Civi_Circle_User
  const apiEmployees = useMemo(() => {
    const membersData = membersResponse?.members ?? [];
    // If API returns no data or is undefined, return empty array
    if (
      !membersData ||
      !Array.isArray(membersData) ||
      membersData.length === 0
    ) {
      return [];
    }

    return membersData.map((member: any, index: number) => {
      // Get profile image from API - check multiple possible field names
      const profileImage =
        member.profileimg ??
        member.profile_img ??
        member.profileimage ??
        member.profile_image ??
        member.profilephoto ??
        member.profile_photo ??
        member.member_profile_image ??
        member.avatar ??
        null;

      // Helper function to check if string is a valid URL
      const isValidURL = (str: string | null | undefined): boolean => {
        if (!str) {
          return false;
        }
        try {
          new URL(str);
          return true;
        } catch {
          return false;
        }
      };

      // Use API profile image if available, otherwise use null (will show initials)
      let avatar: string | null = null;

      if (profileImage) {
        if (isValidURL(profileImage)) {
          // Already a full URL
          avatar = profileImage;
        } else {
          // Relative path - construct full URL
          // Check if it starts with /, if not add it
          const imagePath = profileImage.startsWith("/")
            ? profileImage
            : `/${profileImage}`;
          const apiBaseUrl =
            import.meta.env.VITE_API_BASE_URL ?? "http://localhost:8000";
          avatar = `${apiBaseUrl}${imagePath}`;
        }
      }

      // Extract gov_skill from member data if available, otherwise use empty array
      const expertise = member.gov_skill
        ? Array.isArray(member.gov_skill)
          ? member.gov_skill
          : [member.gov_skill]
        : [];

      return {
        id: member.member_id ?? member.id ?? index,
        name: member.member_full_name ?? "Unknown",
        position: member.position ?? "",
        department: member.department ?? "",
        location: member.cityaddress ?? "",
        email: member.member_email ?? "",
        phone:
          member.member_phone_number ??
          member.phone ??
          member.member_phone ??
          "",
        avatar: avatar,
        verified: member.verified ?? member.member_verified ?? false,
        expertise: expertise.length > 0 ? expertise : [], // Ensure it's always an array
        status: "Active",
      };
    });
  }, [membersResponse?.members]);

  // Use only API employees
  const allEmployees = useMemo(() => {
    return apiEmployees;
  }, [apiEmployees]);

  // Get skills from API - use skill field from API response
  const allExpertise = useMemo(() => {
    if (
      !govSkillsData ||
      !Array.isArray(govSkillsData) ||
      govSkillsData.length === 0
    ) {
      return [];
    }
    // Extract skill names from API response
    // API returns array of objects with 'skill' field
    return govSkillsData
      .map((item: any) => {
        if (typeof item === "string") {
          return item;
        }
        // Extract skill field from response object
        const skill = item.skill ?? item.name ?? item.title ?? "";
        return skill.trim();
      })
      .filter((skill: string) => skill && skill.length > 0);
  }, [govSkillsData]);

  const toggleExpertise = (exp: string) => {
    setSelectedExpertise(prev =>
      prev.includes(exp) ? prev.filter(e => e !== exp) : [...prev, exp]
    );
  };

  const clearAllFilters = () => {
    setSelectedExpertise([]);
    setVerifiedOnly(false);
    setSearchQuery("");
  };

  // Filter API employees based on search and filters
  const filteredEmployees = useMemo(() => {
    return apiEmployees.filter(employee => {
      const matchesSearch =
        (!searchQuery ||
          employee.name.toLowerCase().includes(searchQuery.toLowerCase())) ??
        employee.position.toLowerCase().includes(searchQuery.toLowerCase()) ??
        employee.department.toLowerCase().includes(searchQuery.toLowerCase()) ??
        employee.expertise.some((e: string) =>
          e.toLowerCase().includes(searchQuery.toLowerCase())
        );

      const matchesExpertise =
        selectedExpertise.length === 0 ||
        selectedExpertise.some(exp => employee.expertise.includes(exp));

      const matchesVerified = !verifiedOnly || employee.verified;

      const matchesLocation = true;

      return (
        matchesSearch &&
        matchesExpertise &&
        matchesVerified &&
        matchesLocation
      );
    });
  }, [
    apiEmployees,
    searchQuery,
    selectedExpertise,
    verifiedOnly,
  ]);

  const hasActiveFilters =
    selectedExpertise.length > 0 ||
    verifiedOnly;

  return (
    <div className="min-h-screen bg-background pb-24">
      {/* Header */}
      <header className="sticky top-0 z-50 border-b border-border bg-card shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="mb-4 flex items-center gap-3">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate(CitycollabNavigationRoutes.NEW_DESIGN)}
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div className="flex-1">
              <div className="flex items-center gap-2">
                <Shield className="h-5 w-5 text-primary" />
                <h1 className="text-2xl font-bold text-foreground">
                  Central Directory
                </h1>
              </div>
              <p className="mt-1 text-sm text-muted-foreground">
                Secure, centralized directory of all organization employees
              </p>
            </div>
            <Badge
              variant="outline"
              className="hidden items-center gap-1 md:flex"
            >
              <BadgeCheck className="h-3.5 w-3.5 text-emerald-500" />
              <span>
                {allEmployees.filter((e: any) => e.verified).length} Verified
              </span>
            </Badge>
          </div>

          {/* Search Bar */}
          <div className="flex flex-col gap-3 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search by name, position..."
                value={searchQuery}
                onChange={e => setSearchQuery(e.target.value)}
                className="h-11 bg-muted/50 pl-10"
              />
            </div>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-6 md:py-8">
        <div className="flex flex-col gap-6 lg:flex-row">
          {/* Filters Sidebar */}
          <aside className="w-full space-y-4 lg:w-72">
            <Card>
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2 text-sm">
                    <Filter className="h-4 w-4" />
                    Search Filters
                  </CardTitle>
                  {hasActiveFilters && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={clearAllFilters}
                      className="h-7 text-xs"
                    >
                      Clear All
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="space-y-5">
                {/* Quick Find - Organization Only Toggle */}
                <div className="flex items-center justify-between border-b py-2">
                  <Label
                    htmlFor="nearby-toggle"
                    className="flex cursor-pointer items-center gap-2 text-sm"
                  >
                    <MapPin className="h-4 w-4 text-primary" />
                    Organization Only
                  </Label>
                  <Switch
                    id="nearby-toggle"
                    checked={nearbyOnly}
                    onCheckedChange={setNearbyOnly}
                  />
                </div>

                {/* Verified Only Toggle */}
                <div className="flex items-center justify-between border-b py-2">
                  <Label
                    htmlFor="verified-toggle"
                    className="flex cursor-pointer items-center gap-2 text-sm"
                  >
                    <BadgeCheck className="h-4 w-4 text-emerald-500" />
                    Verified Only
                  </Label>
                  <Switch
                    id="verified-toggle"
                    checked={verifiedOnly}
                    onCheckedChange={setVerifiedOnly}
                  />
                </div>

                {/* Expertise */}
                <div className="space-y-3">
                  <h4 className="flex items-center gap-2 text-sm font-medium">
                    <Briefcase className="h-4 w-4" />
                    Expertise
                  </h4>
                  {isLoadingSkills ? (
                    <p className="text-sm text-muted-foreground">
                      Loading skills...
                    </p>
                  ) : allExpertise.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {allExpertise.slice(0, 8).map(exp => (
                        <Badge
                          key={exp}
                          variant={
                            selectedExpertise.includes(exp)
                              ? "default"
                              : "outline"
                          }
                          className="cursor-pointer text-xs"
                          onClick={() => toggleExpertise(exp)}
                        >
                          {exp}
                          {selectedExpertise.includes(exp) && (
                            <X className="ml-1 h-3 w-3" />
                          )}
                        </Badge>
                      ))}
                    </div>
                  ) : (
                    <p className="text-sm text-muted-foreground">
                      No expertise data available
                    </p>
                  )}
                </div>
              </CardContent>
            </Card>

            {/* Directory Stats */}
            <Card>
              <CardContent className="p-4">
                <h4 className="mb-3 text-sm font-medium">Directory Stats</h4>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">
                      Total Employees
                    </span>
                    <span className="font-medium">{totalMembersCount}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-muted-foreground">Verified</span>
                    <span className="font-medium text-emerald-600">
                      {verifiedMembersCount}
                    </span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </aside>

          {/* Main Content */}
          <div className="flex-1">
            {/* Results Header */}
            <div className="mb-4 flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing{" "}
                <span className="font-medium text-foreground">
                  {filteredEmployees.length}
                </span>{" "}
                of {allEmployees.length} employees
              </p>
            </div>

            {/* Employee Cards */}
            {isLoadingMembers ? (
              <Card>
                <CardContent className="p-12 text-center">
                  <p className="text-muted-foreground">Loading members...</p>
                </CardContent>
              </Card>
            ) : (
              <div className="grid gap-4">
                {filteredEmployees.map(employee => (
                  <Card
                    key={employee.id}
                    className="transition-shadow hover:shadow-md"
                  >
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div className="relative">
                          <Avatar className="h-16 w-16">
                            <AvatarImage
                              src={employee.avatar ?? undefined}
                              alt={employee.name}
                            />
                            <AvatarFallback className="bg-primary text-lg text-primary-foreground">
                              {employee.name
                                .split(" ")
                                .map((n: string) => n[0])
                                .join("")}
                            </AvatarFallback>
                          </Avatar>
                          {employee.verified && (
                            <div className="absolute -bottom-1 -right-1 rounded-full bg-emerald-500 p-0.5 text-white">
                              <BadgeCheck className="h-4 w-4" />
                            </div>
                          )}
                        </div>
                        <div className="min-w-0 flex-1">
                          <div className="mb-1">
                            <div>
                              <h3 className="text-lg font-semibold text-foreground">
                                {employee.name}
                              </h3>
                              <p className="text-sm text-muted-foreground">
                                {employee.position}
                              </p>
                            </div>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Building2 className="h-3.5 w-3.5" />
                              {employee.department}
                            </span>
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5" />
                              {employee.location}
                            </span>
                          </div>
                          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                            <span className="flex items-center gap-1">
                              <Mail className="h-3.5 w-3.5" />
                              {employee.email}
                            </span>
                            <span className="flex items-center gap-1">
                              <Phone className="h-3.5 w-3.5" />
                              {employee.phone}
                            </span>
                          </div>
                          <div className="mt-3 flex flex-wrap gap-1.5">
                            {employee.expertise.map((skill: string) => (
                              <Badge
                                key={skill}
                                variant="secondary"
                                className="text-xs"
                              >
                                {skill}
                              </Badge>
                            ))}
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                ))}

                {filteredEmployees.length === 0 && (
                  <Card>
                    <CardContent className="p-12 text-center">
                      <Search className="mx-auto mb-4 h-12 w-12 text-muted-foreground/50" />
                      <p className="mb-2 text-lg font-medium text-foreground">
                        No employees found
                      </p>
                      <p className="mb-4 text-muted-foreground">
                        Try adjusting your search or filter criteria
                      </p>
                      <Button variant="outline" onClick={clearAllFilters}>
                        Clear All Filters
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            )}
          </div>
        </div>
      </main>

      <MainNavigation />
    </div>
  );
};

export default Directory;
